import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCreditStats } from '../store/reducers/index';
import apiFetch from '../utils/api';

const API_BASE_URL = `${process.env.REACT_APP_DOCS_API_KEY}`;

export const useUserLoaded = () => {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          // Silently skip if no token (user not logged in yet)
          return;
        }
        
        // Always fetch user data to ensure we have the latest information
        const response = await apiFetch(`https://${API_BASE_URL}/api/users/me`, {
          headers: {
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        });
  
        if (!response.ok) {
          throw new Error('Failed to fetch user details');
        }
  
        const data = await response.json();
        
        // Store user data in session storage
        sessionStorage.setItem('user_id', data.id);

        dispatch({ type: 'UPDATE_USER', payload: data });
        
        setIsLoaded(true);
        
      } catch (err) {
        console.error("Error fetching user:", err.message);
        
        if (sessionStorage.getItem('user_id')) {
          setIsLoaded(true);
        }
      }
    };
  
    fetchUser();
  }, []);
  
  return isLoaded;
};

export const useCompany = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;
    
    const fetchCompany = async () => {
      const userId = sessionStorage.getItem('user_id');
      
      if (!userId) {
        return;
      }
      
      try {
        const response = await apiFetch(
          `https://${API_BASE_URL}/api/items/company`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            }
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to fetch company');
        }
  
        const data = await response.json();
        
        // Get the first item if it's an array, or use the data directly
        const companyData = data.data ? 
          (Array.isArray(data.data) ? data.data[0] : data.data) : 
          (Array.isArray(data) ? data[0] : data);
        
        dispatch({ type: 'SET_COMPANY', payload: companyData });
      } catch (err) {
        console.error("Error fetching company:", err.message);
        dispatch({ type: 'SET_NEEDS_COMPANY_CREATION', payload: true });
      }
    };
  
    fetchCompany();
  }, [dispatch, isUserLoaded]);
};

export const useAgents = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;
    
    const fetchAgents = async () => {
      const userId = sessionStorage.getItem('user_id');
      
      if (!userId) {
        console.warn('No user_id found in sessionStorage, skipping agent fetch');
        return;
      }
      
      try {
        
        const response = await apiFetch(
          `https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/agents?filter%5Buser_id%5D%5B_eq%5D=${userId}`, 
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            }
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to fetch agents');
        }
  
        const data = await response.json();
        
        const agentsData = data.data || (Array.isArray(data) ? data : []);
        
        dispatch({ type: 'SET_AGENTS', payload: agentsData });
        
        // Note: Default agent selection is now handled in Dashboard.jsx per export index
      } catch (err) {
        console.error("Error fetching agents:", err.message);
      }
    };
  
    fetchAgents();
  }, [dispatch, isUserLoaded]);
};

export const useSenders = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;
    
    const fetchSenders = async () => {
      const userId = sessionStorage.getItem('user_id');
      
      if (!userId) {
        console.warn('No user_id found in sessionStorage, skipping sender fetch');
        return;
      }
      
      try {
        
        const response = await apiFetch(
          `https://${API_BASE_URL}/api/items/senders?filter%5Buser_id%5D%5B_eq%5D=${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            }
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to fetch senders');
        }
  
        const data = await response.json();
        
        const sendersData = data.data || (Array.isArray(data) ? data : []);
        
        dispatch({ type: 'SET_SENDERS', payload: sendersData });
      } catch (err) {
        console.error("Error fetching senders:", err.message);
      }
    };
  
    fetchSenders();
  }, [dispatch, isUserLoaded]);
};

export const useReceivers = () => {
  const dispatch = useDispatch();
  const isUserLoaded = useUserLoaded();

  useEffect(() => {
    if (!isUserLoaded) return;
    
    const fetchReceivers = async () => {
      const userId = sessionStorage.getItem('user_id');
      
      if (!userId) {
        console.warn('No user_id found in sessionStorage, skipping receiver fetch');
        return;
      }
      
      try {
        
        const response = await apiFetch(
          `https://${API_BASE_URL}/api/items/receivers?filter%5Buser_id%5D%5B_eq%5D=${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            }
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to fetch receivers');
        }
  
        const data = await response.json();
        
        const receiversData = data.data || (Array.isArray(data) ? data : []);
        
        dispatch({ type: 'SET_RECEIVERS', payload: receiversData });
      } catch (err) {
        console.error("Error fetching receivers:", err.message);
      }
    };
  
    fetchReceivers();
  }, [dispatch, isUserLoaded]);
};

export const useCreditStats = () => {
  const dispatch = useDispatch();
  const { current, usage, exportCost, loading, error } = useSelector(state => state.creditStats);

  const refetch = () => {
    dispatch(fetchCreditStats());
  };

  useEffect(() => {
    dispatch(fetchCreditStats());
  }, [dispatch]);

  return { current, usage, exportCost, loading, error, refetch };
};
